// PHASE_1 / B0 — declarative zod-driven resource form (ADR-015 frontend kit; [D6]).
//
// The single canonical create/edit form every P1+ entity screen reuses. It is
// the DECLARATIVE layer over the lower-level <EntityForm> primitive: instead of
// a render-prop where the host hand-renders each input, the host passes a
// `fields` config (name + i18n label + type) and ResourceForm renders the
// labeled inputs, wires aria-describedby error association, and validates on
// submit against a Zod-style validator (the consuming app owns the schema —
// e.g. an @novemai/*-sdk validator or a local z.object, so the shared library
// takes no hard zod dependency).
//
// SKIN: this component owns NO class vocabulary of its own. It renders through
// the primitives that already carry the design language — <Input>, <Textarea>,
// <Select>, <Button>, and the .nai-form-field frame. It shipped the other way
// round until 2026-08-03: a hand-rolled <select> and <button> plus five
// `nai-resource-form-*` class names that styles.css never defined, which (with
// Tailwind's preflight stripping button chrome) rendered the submit as plain
// text and the dropdown as a naked 20px control on all thirteen consuming
// surfaces. Add a field TYPE here, never a field SKIN.
//
// i18n: every label/placeholder is an already-localized string the host passes
// (ADR-006 — no literals in this library). Money: the optional `formatMoney`
// hook lets a read-only computed-money field render through the app's <Money>
// formatter (minor-units → localized string) WITHOUT this library importing the
// i18n/money runtime — Phase 1 carries hours/days as decimals, but the hook is
// here so P2 money-bearing forms reuse the same component (CLAUDE.md money
// invariant: display-only formatting, storage stays minor-units).

import { type ReactNode } from "react";

import { Button } from "../Button/Button.tsx";
import {
  EntityForm,
  type FieldErrors,
  type SafeParseValidator,
} from "../EntityForm/EntityForm.tsx";
import { HelpHint } from "../HelpHint/HelpHint.tsx";
import { Input } from "../Input/Input.tsx";
import { Select } from "../Select/Select.tsx";
import { Textarea } from "../Textarea/Textarea.tsx";

/** A select option (value + already-localized label). */
export interface ResourceFieldOption {
  readonly value: string;
  readonly label: string;
}

/** One declarative field in the form. */
export interface ResourceField {
  /** Field name — the FormData key + the validator path[0] + the error map key. */
  readonly name: string;
  /** Already-localized label (host passes t("...")). */
  readonly label: string;
  /**
   * Input kind. `text` / `email` / `number` / `date` → <Input>; `textarea` →
   * <Textarea>; `select` → a native <select> over `options`; `money_display`
   * → a READ-ONLY formatted money value (see `formatMoney`), never an editable
   * money input (storage is minor-units; the form never collects a float).
   */
  readonly type?: "text" | "email" | "number" | "date" | "textarea" | "select" | "money_display";
  /** Already-localized placeholder. */
  readonly placeholder?: string;
  /** Options for `type: "select"`. */
  readonly options?: readonly ResourceFieldOption[];
  /** Whether the field is required (sets the input's `required` attr — UX only). */
  readonly required?: boolean;
  /**
   * Disables the editable control while keeping the form's submit affordance
   * available. Useful when a caller must replay an already-frozen mutation
   * intent rather than accept changed field values.
   */
  readonly disabled?: boolean;
  /** Default value for create/edit prefill. */
  readonly defaultValue?: string;
  /**
   * For `type: "money_display"` — the integer minor units to format read-only.
   * Paired with `currency`. The host wires `formatMoney` to its <Money>/i18n.
   */
  readonly minorUnits?: number;
  /** ISO-4217 currency for `type: "money_display"`. */
  readonly currency?: string;
  /**
   * Already-localized explanation behind a "?" beside the label — for a field
   * whose name cannot carry its own meaning ("Model", "Acquisition cost (minor
   * units)"). Requires `helpLabel`.
   *
   * This descriptor is the ONLY way a ResourceForm field can be configured, so
   * without these two the whole family of modal forms built on it — asset
   * registration, the resource create/edit dialogs — had no place to put help
   * at all, whatever the design system offered.
   */
  readonly help?: string;
  /** Already-localized accessible name of the help trigger. */
  readonly helpLabel?: string;
}

export interface ResourceFormProps<TValue> {
  /** Declarative field config rendered in order. */
  readonly fields: readonly ResourceField[];
  /** Zod-style validator run against the collected FormData on submit. */
  readonly validator: SafeParseValidator<TValue>;
  /** Called with the validated, typed value on a successful parse. */
  readonly onValidSubmit: (value: TValue) => void | Promise<void>;
  /** Already-localized submit-button label. */
  readonly submitLabel: string;
  /**
   * Dismiss affordance. Eight modals are built on this component and none of
   * them could render one, so every asset/benefit dialog offered a submit and
   * nothing else — Escape and the scrim did close them, but a first-time user
   * has no way to know that from looking at the screen.
   *
   * A single object rather than a loose `onCancel` + `cancelLabel` pair: half
   * a cancel button (a handler with no label, or a label with no handler) is
   * not a state any caller wants, so the type refuses to express it.
   */
  readonly cancel?: {
    readonly label: string;
    readonly onCancel: () => void;
  };
  /**
   * Display-only money formatter (minor-units → localized string). Supplied by
   * the host (wraps @novemai/i18n-sdk formatMoney + the resolved locale). Only
   * invoked for `type: "money_display"` fields. Keeps this library free of an
   * i18n/money runtime dependency (CLAUDE.md: storage stays minor-units).
   */
  readonly formatMoney?: (minorUnits: number, currency: string) => string;
  /**
   * Optional override for collecting/coercing FormData before validation
   * (numbers, checkboxes). Defaults to EntityForm's Object.fromEntries.
   */
  readonly collect?: (formData: FormData) => unknown;
  readonly className?: string;
  /**
   * Optional page layout. Both nodes stay inside the native form; the action
   * keeps EntityForm's validation and submitting state when placed in a header.
   * Modal consumers keep the default fields-then-actions layout.
   */
  readonly renderLayout?: (slots: {
    readonly fields: ReactNode;
    readonly actions: ReactNode;
  }) => ReactNode;
}

function renderField(
  field: ResourceField,
  errors: FieldErrors,
  formatMoney: ResourceFormProps<unknown>["formatMoney"],
): ReactNode {
  const errorId = `${field.name}-error`;
  const error = errors[field.name];
  const describedBy = error !== undefined ? errorId : undefined;

  const control = ((): ReactNode => {
    if (field.type === "money_display") {
      // Read-only formatted money — never an editable float input. Phase 1
      // forms don't use this (hours/days are decimals); P2 money forms do.
      const formatted =
        field.minorUnits !== undefined && field.currency !== undefined && formatMoney
          ? formatMoney(field.minorUnits, field.currency)
          : "";
      return (
        <output id={field.name} data-slot="resource-form-money">
          {formatted}
        </output>
      );
    }
    if (field.type === "textarea") {
      return (
        <Textarea
          id={field.name}
          name={field.name}
          placeholder={field.placeholder}
          required={field.required}
          disabled={field.disabled}
          defaultValue={field.defaultValue}
          error={error !== undefined}
          aria-describedby={describedBy}
        />
      );
    }
    if (field.type === "select") {
      return (
        <Select
          id={field.name}
          name={field.name}
          required={field.required}
          disabled={field.disabled}
          defaultValue={field.defaultValue ?? ""}
          error={error !== undefined}
          aria-describedby={describedBy}
          data-slot="resource-form-select"
        >
          {(field.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      );
    }
    return (
      <Input
        id={field.name}
        name={field.name}
        type={field.type ?? "text"}
        placeholder={field.placeholder}
        required={field.required}
        disabled={field.disabled}
        defaultValue={field.defaultValue}
        error={error !== undefined}
        aria-describedby={describedBy}
      />
    );
  })();

  return (
    <div key={field.name} data-slot="resource-form-field" className="nai-form-field">
      <div className="nai-form-field-labelrow">
        <label htmlFor={field.name} className="nai-form-field-label">
          {field.label}
        </label>
        {/* helpLabel gates the render for the same reason it does in FormField:
            an unlabelled icon button is a screen-reader dead end, and this
            library has no i18n runtime to default the name from. */}
        {field.help !== undefined && field.helpLabel !== undefined && (
          <HelpHint label={field.helpLabel}>{field.help}</HelpHint>
        )}
      </div>
      {control}
      {error !== undefined && (
        <p id={errorId} role="alert" className="nai-form-field-error">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Declarative, zod-driven create/edit form. Renders labeled inputs from
 * `fields`, validates on submit, surfaces per-field errors (already localized
 * by the host's validator messages), and hands the typed value to
 * `onValidSubmit`. Composes <EntityForm> for the form/validation plumbing.
 */
export function ResourceForm<TValue>(
  props: ResourceFormProps<TValue>,
): React.ReactElement {
  const {
    fields,
    validator,
    onValidSubmit,
    submitLabel,
    cancel,
    formatMoney,
    collect,
    className,
    renderLayout,
  } = props;

  return (
    <EntityForm<TValue>
      validator={validator}
      onValidSubmit={onValidSubmit}
      {...(collect !== undefined ? { collect } : {})}
      data-component="resource-form"
      {...(className !== undefined && { className })}
    >
      {({ errors, submitting }): ReactNode => {
        const fieldNodes = fields.map((field) => renderField(field, errors, formatMoney));
        const actions = (
          <div data-slot="resource-form-actions">
            {cancel !== undefined && (
              // type="button": inside a <form> the default is "submit", so a
              // cancel that forgot it would post the form it means to abandon.
              <Button
                type="button"
                variant="secondary"
                onClick={cancel.onCancel}
                disabled={submitting}
                data-slot="resource-form-cancel"
              >
                {cancel.label}
              </Button>
            )}
            {/* `disabled`, not `loading`: Button drops the NATIVE disabled attr
                while loading (to keep the announcement focusable), and an
                aria-disabled-only submit stays clickable — a second click would
                fire a duplicate write. */}
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
              aria-busy={submitting || undefined}
              data-slot="resource-form-submit"
            >
              {submitLabel}
            </Button>
          </div>
        );
        return renderLayout
          ? renderLayout({ fields: fieldNodes, actions })
          : <>{fieldNodes}{actions}</>;
      }}
    </EntityForm>
  );
}
